import { Popover, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { UserIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { UserIcon as UserIconSolid } from '@heroicons/react/24/solid'
import { FC, Fragment } from 'react'

import { useAuth } from '@/api/models/auth/hooks/useAuth'
import { cn } from '@/app/utils'
import { ButtonSkeleton } from '@/components/shared/skeleton'

import ExitLogoutSvg from '../assets/ExitLogout.svg'

type Props = {
  className?: string
  onOpenProfile: () => void
  isLoading: boolean
}

const UserProfilePopover: FC<Props> = ({
  className,
  onOpenProfile,
  isLoading
}) => {
  const { logout } = useAuth()

  if (isLoading) {
    return <ButtonSkeleton width={70} height={44} />
  }

  return (
    <Popover className={cn('relative', className)}>
      {({ open, close }) => (
        <>
          <Popover.Button
            aria-label="Открыть выпадающее меню"
            className={cn(
              'group flex items-center justify-center space-x-4 rounded-100 bg-white p-10 transition-colors hover:bg-bronze-40 hover:text-grey-10',
              'xs:p-6',
              open && 'bg-bronze-40 text-grey-10'
            )}
          >
            <div className="relative h-24 w-24 xs:h-20 xs:w-20">
              <UserIcon
                className={cn(
                  'absolute inset-0 h-full w-full transition-opacity',
                  open && 'opacity-0'
                )}
              />
              <UserIconSolid
                className={cn(
                  'absolute inset-0 h-full w-full opacity-0 transition-opacity',
                  open && 'opacity-1'
                )}
              />
            </div>
            <ChevronDownIcon className="h-20 w-20 xs:hidden" />
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              className={cn(
                'absolute right-0 z-10',
                'xs:fixed xs:bottom-0 xs:left-0 xs:right-0 xs:top-60 xs:z-50 '
              )}
            >
              <div
                className={cn(
                  'relative mt-8 flex max-h-full w-[160px] flex-col overflow-hidden rounded-12 bg-grey-10 shadow-[0px_2px_24px_0px_rgba(0,0,0,0.10)]',
                  'xs:mt-0 xs:h-full xs:max-h-full xs:w-auto xs:rounded-0 xs:px-16 xs:py-32 xs:shadow-none'
                )}
              >
                <button
                  aria-label="Закрыть профиль"
                  title="Закрыть"
                  className={cn(
                    'absolute ml-auto hidden h-40 w-40 items-center justify-center rounded-100 bg-[rgba(96,107,118,0.10)] p-8 transition-colors hover:bg-grey-30',
                    'xs:right-16 xs:top-16 xs:flex xs:h-28 xs:w-28 xs:p-4'
                  )}
                  onClick={close}
                >
                  <XMarkIcon className="h-24 w-24 text-bronze-70" />
                </button>

                <div className="max-h-full grow">
                  <button
                    className="group flex h-60 w-full items-center p-20 pb-12 font-pro transition-colors text-18 hover:text-grey-60"
                    onClick={onOpenProfile}
                  >
                    <UserIconSolid className="h-24 w-24 text-grey-70 group-hover:text-grey-60 xs:h-20 xs:w-20" />
                    <span className="ml-8">Профиль</span>
                  </button>

                  <button
                    className="group flex h-60 w-full items-center p-20 pt-12 font-pro transition-colors text-18 hover:text-grey-60"
                    onClick={logout}
                  >
                    <ExitLogoutSvg className="text-grey-70 transition-colors group-hover:text-grey-60" />
                    <span className="ml-8">Выйти</span>
                  </button>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  )
}

export default UserProfilePopover
